import { useToast } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from 'shared/api/comparison';
import { isAuthenticatedUserSelector } from 'shared/selectors/isAuthenticatedUserSelector';
import { getNewbuildingId } from 'shared/selectors/newbuildingHeader';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareButtonWithTooltip } from './CompareButtonWithTooltip';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import { trackAddNewbuildingToComparison, trackRemoveNewbuildingFromComparison } from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingHeaderCompareButtonProps {
  withOnboarding?: boolean;
}

export const NewbuildingHeaderCompareButton: React.FC<INewbuildingHeaderCompareButtonProps> = ({
  withOnboarding = false,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const newbuildingId = useSelector(getNewbuildingId) as number;
  const isAuthenticated = useSelector(isAuthenticatedUserSelector);
  const [isAdded, setIsAdded] = React.useState(false);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;

  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isCompareEnabled);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: <CompareToastContent text={result.text} comparisonUrl={result.comparisonUrl} />,
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextValue = !isAdded;

    if (nextValue && !isAuthenticated) {
      window.waitForLoginPopup?.();

      return;
    }

    setIsAdded(nextValue);

    try {
      const result = nextValue
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      showComparisonToast(result);

      if (result.success === false) {
        setIsAdded(!nextValue);

        return;
      }

      if (nextValue) {
        trackAddNewbuildingToComparison(newbuildingId);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId);
      }
    } catch (_error) {
      setIsAdded(!nextValue);
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [isAdded, isAuthenticated, httpApi, logger, newbuildingId, showComparisonToast]);

  if (!isCompareEnabled) {
    return null;
  }

  if (!withOnboarding || !open) {
    return <CompareButtonWithTooltip isAdded={isAdded} onClick={handleClick} />;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      <CompareButtonWithTooltip disabled={true} isAdded={isAdded} onClick={handleClick} />
    </CompareButtonOnboarding>
  );
};
